<template>
  <div class="mt-6 flex flex-col gap-2 bg-gray-800 p-6 text-white">
    <h2 class="font-sans text-[24px] font-medium leading-7">
      {{ $t('user_area.title') }}
    </h2>
    <ul>
      <template v-for="(area, i) in userArea" :key="i">
        <li v-if="area.active" class="flex cursor-pointer items-center">
          <SFLink
            v-if="area.link"
            class="block w-full gap-x-3 py-6"
            :to="area.link"
            @click.stop.prevent="emit('clickLink')"
          >
            <component :is="area.icon" :name="area.icon" :width="20" />
            <span class="text-lg">{{ area.label }}</span>
          </SFLink>
          <button
            v-else
            class="flex w-full gap-x-3 py-6 text-left"
            @click.stop.prevent="userAreaAction(area)"
          >
            <component :is="area.icon" :name="area.icon" :width="20" />
            <span class="text-lg">{{ area.label }}</span>
          </button>
        </li>
      </template>
    </ul>
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from '#i18n'
import { routeList } from '~/utils/route'
import { useAuthentication, useRouteHelpers } from '~/composables'
import type { RouteLocationAsPathGeneric } from '#vue-router'
import { useSlideIn } from '#storefront-ui'
import { SFLink } from '#storefront-ui/components'
import { useUser } from '#storefront/composables'

interface UserAreaItem {
  label: string
  icon: string
  link?: string | RouteLocationAsPathGeneric
  action?: 'log-out' | 'lang'
  active: boolean
  children?: UserAreaItem[]
}

const emit = defineEmits<{ clickLink: []; scrollToTop: [] }>()

const { t } = useI18n()
const { logout } = useAuthentication('logout')
const { getLocalizedRoute } = useRouteHelpers()
const { isLoggedIn, user } = useUser()

const accountLink = computed(() => {
  if (isLoggedIn.value) {
    return getLocalizedRoute(routeList.profile)
  }
  return getLocalizedRoute(routeList.signin)
})

const userLabel = computed(() =>
  isLoggedIn.value
    ? t('user_area.my_account', { user: user.value?.firstName })
    : t('user_area.auth'),
)

const userArea = computed<UserAreaItem[]>(() => [
  {
    label: userLabel.value,
    icon: 'IconUser',
    link: accountLink.value,
    active: true,
  },
  {
    label: t('user_area.store'),
    icon: 'IconLocationMenu',
    link: getLocalizedRoute(routeList.storefinder),
    active: true,
  },
  {
    label: t('account_tabs.orders'),
    icon: 'IconOrderMulti',
    link: getLocalizedRoute(routeList.orders),
    active: isLoggedIn.value,
  },
  {
    label: t('user_area.newsletter'),
    icon: 'IconLetter',
    link: getLocalizedRoute(routeList.subscriptionOverview),
    action: 'log-out',
    active: isLoggedIn.value,
  },
  {
    label: t('user_area.country'),
    icon: 'IconGlobe',
    action: 'lang',
    active: true,
  },

  {
    label: t('user_area.help_service'),
    icon: 'IconInfoOutline',
    link: getLocalizedRoute('/s/contact-us'),
    active: true,
  },
  {
    label: t('global.sign_out'),
    icon: 'IconLogout',
    action: 'log-out',
    active: !!user.value,
  },
])

const { toggle } = useSlideIn('ShopSwitcherSlideIn')

const userAreaAction = async (area: UserAreaItem) => {
  if (area.action === 'lang') {
    toggle()
  } else {
    await logout()
  }
}
</script>
<style scoped></style>
